import React, { useState } from "react";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useData } from "@/context/DataContext";
import { formatApiErrorDetail } from "@/lib/api";
import { hoyISO } from "@/lib/zona";
import { dec2, parseImporte } from "@/lib/gastos";
import { Sheet } from "@/pages/Familia";
import { Avatar } from "@/components/personas/Avatar";

const input = "w-full min-h-[52px] px-4 rounded-xl bg-white border border-[#E5DFD5] text-base text-[#1C2421] focus:outline-none focus:ring-4 focus:ring-[#3A6B56]/15";
const Campo = ({ label, children }) => (
  <label className="block"><span className="text-sm font-semibold text-[#4A5550]">{label}</span><div className="mt-1">{children}</div></label>
);

const PersonaIngreso = ({ value, onChange }) => {
  const { familyMembers = [] } = useData();
  const b = (sel) => `min-h-[48px] rounded-xl border-2 text-sm font-semibold ${sel ? "bg-[#3A6B56] border-[#3A6B56] text-white" : "bg-white border-[#E5DFD5]"}`;
  return (
    <div>
      <p className="text-sm font-semibold text-[#4A5550]">¿Quién lo cobra? <span className="font-normal text-[#6E7A74]">(opcional)</span></p>
      <div className="flex flex-wrap gap-2 mt-2">
        <button type="button" data-testid="ingreso-persona-ninguna" onClick={() => onChange("")} className={`${b(!value)} px-4`}>Sin asignar</button>
        {familyMembers.map((p) => (
          <button key={p.id} type="button" data-testid={`ingreso-persona-${p.id}`} onClick={() => onChange(p.id)} className={`${b(value === p.id)} pl-1.5 pr-4 inline-flex items-center gap-2`}>
            <Avatar persona={p} size="sm" /> <span aria-hidden="true">{p.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// Ingreso: siempre a mano. Si se pasa `ingreso`, permite editarlo y eliminarlo.
export const IngresoForm = ({ ingreso, personaInicial, onClose, onSaved }) => {
  const { guardarIngreso, borrarIngreso } = useData();
  const [f, setF] = useState(() => ingreso
    ? { ...ingreso, importe: dec2(ingreso.importe), family_member_id: ingreso.family_member_id || "" }
    : { fecha: hoyISO(), concepto: "", importe: "", family_member_id: personaInicial || "", notas: "" });
  const [busy, setBusy] = useState(false);
  const [borrar, setBorrar] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const guardar = async () => {
    const importe = parseImporte(f.importe);
    if (!importe || importe <= 0) return toast.error("Indica el importe del ingreso.");
    if (!f.concepto.trim()) return toast.error("Indica el concepto del ingreso.");
    if (f.fecha > hoyISO() && f.fecha !== ingreso?.fecha) return toast.error("Un ingreso es dinero ya cobrado: la fecha no puede ser posterior a hoy.");
    setBusy(true);
    try {
      const x = await guardarIngreso(ingreso?.id, { fecha: f.fecha, concepto: f.concepto, importe, family_member_id: f.family_member_id || null, notas: f.notas });
      toast.success(ingreso ? "Ingreso actualizado" : "Ingreso anotado");
      onSaved?.(x);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar el ingreso.");
    } finally { setBusy(false); }
  };
  const eliminar = async () => {
    try { await borrarIngreso(ingreso.id); toast.success("Ingreso eliminado"); onClose(); }
    catch { toast.error("No se pudo eliminar el ingreso."); }
  };

  return (
    <Sheet onClose={onClose} testid="ingreso-form">
      <h2 className="font-heading text-2xl font-bold text-[#1C2421] pr-12">{ingreso ? "Editar ingreso" : "Nuevo ingreso"}</h2>
      <p data-testid="ingreso-aviso-manual" className="mt-2 bg-[#F2F6F4] text-[#2E5645] rounded-xl p-3 text-sm flex gap-2">
        <Icons.PenLine size={18} className="shrink-0" /> Los ingresos se anotan a mano. Si tienes una foto o justificante del ingreso, copia tú aquí los datos.
      </p>
      <div className="space-y-4 mt-5">
        <Campo label="Importe (€)">
          <input data-testid="ingreso-importe" inputMode="decimal" value={f.importe} onChange={(e) => set("importe")(e.target.value)} placeholder="0,00" className={`${input} text-2xl font-bold`} />
        </Campo>
        <Campo label="Concepto">
          <input data-testid="ingreso-concepto" value={f.concepto} onChange={(e) => set("concepto")(e.target.value)} placeholder="Ej. Pensión, nómina, alquiler cobrado…" className={input} />
        </Campo>
        <Campo label="Fecha del cobro">
          <input data-testid="ingreso-fecha" type="date" max={ingreso?.fecha > hoyISO() ? ingreso.fecha : hoyISO()} value={f.fecha} onChange={(e) => set("fecha")(e.target.value)} className={input} />
        </Campo>
        <PersonaIngreso value={f.family_member_id} onChange={set("family_member_id")} />
        <Campo label="Notas (opcional)">
          <textarea data-testid="ingreso-notas" rows={2} value={f.notas || ""} onChange={(e) => set("notas")(e.target.value)} className={`${input} py-3`} />
        </Campo>
      </div>
      <button data-testid="ingreso-guardar" disabled={busy} onClick={guardar} className="mt-6 w-full min-h-[56px] rounded-2xl bg-[#3A6B56] text-white font-bold text-lg disabled:opacity-60">
        {busy ? "Guardando…" : "Guardar ingreso"}
      </button>
      {ingreso && (borrar ? (
        <div className="mt-4 bg-[#FEF2F2] rounded-2xl p-4 space-y-2.5" data-testid="ingreso-borrar-confirmar">
          <p className="font-semibold text-[#1C2421]">¿Eliminar este ingreso?</p>
          <button data-testid="ingreso-borrar-si" onClick={eliminar} className="w-full min-h-[52px] rounded-xl bg-[#B91C1C] text-white font-semibold">Eliminar ingreso</button>
          <button data-testid="ingreso-borrar-cancelar" onClick={() => setBorrar(false)} className="w-full min-h-[48px] text-[#4A5550] font-semibold">Cancelar</button>
        </div>
      ) : (
        <button data-testid="ingreso-eliminar" onClick={() => setBorrar(true)} className="mt-3 w-full min-h-[52px] rounded-2xl bg-white border border-[#E5DFD5] text-[#B91C1C] font-semibold inline-flex items-center justify-center gap-2">
          <Icons.Trash2 size={18} /> Eliminar ingreso
        </button>
      ))}
    </Sheet>
  );
};
